import { workflowPayloadFromWorkflow } from "components/admin/tenant/workflow/workflow-edit";
import { useUpdateWorkflowMutation, WorkflowPayload } from "generated/graphql";
import React from "react";
import { EditableWorkflow } from "../types";
import {
    createWorkflowWriteQueue,
    WorkflowPayloadEdit,
    WorkflowWriteQueue,
} from "./workflow-write-queue";

export interface WorkflowEditor {
    readPayload: () => WorkflowPayload;
    editPayload: (edit: WorkflowPayloadEdit) => Promise<void>;
    saving: boolean;
}

interface QueueForWorkflow {
    workflowId: string;
    queue: WorkflowWriteQueue;
}

const queueForWorkflow = (workflow: EditableWorkflow): QueueForWorkflow => ({
    workflowId: workflow.id,
    queue: createWorkflowWriteQueue(workflowPayloadFromWorkflow(workflow)),
});

export const useWorkflowEditor = (
    workflow: EditableWorkflow,
    refetch: () => void,
): WorkflowEditor => {
    const updateWorkflowMutation = useUpdateWorkflowMutation();
    const [queueState, setQueueState] = React.useState<QueueForWorkflow>(() =>
        queueForWorkflow(workflow),
    );

    if (queueState.workflowId !== workflow.id) {
        setQueueState(queueForWorkflow(workflow));
    }

    const queue = queueState.queue;
    const workflowId = workflow.id;

    React.useEffect(() => {
        queue.adopt(workflowPayloadFromWorkflow(workflow));
    }, [queue, workflow]);

    const readPayload = React.useCallback(() => queue.read(), [queue]);

    const editPayload = React.useCallback(
        (edit: WorkflowPayloadEdit) =>
            queue.edit(edit, {
                send: async payload => {
                    await updateWorkflowMutation.mutateAsync({
                        workflowId,
                        workflow: payload,
                    });
                },
                onSettled: refetch,
            }),
        [queue, updateWorkflowMutation, workflowId, refetch],
    );

    return {
        readPayload,
        editPayload,
        saving: updateWorkflowMutation.isLoading,
    };
};
